import { FC, Fragment, useCallback, useState } from "react"

import { ReactComponent as IconSearch } from "@common_assets/svg/search.svg"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Autocomplete,
  Box,
  Checkbox,
  debounce,
  InputAdornment,
  Stack,
  TextField as MuiTextField,
} from "@mui/material"
import { useMutationCreateGroup } from "api/reactQuery/mutations/groups"
import { useInfiniteQueryClassesStudents } from "api/reactQuery/queries/classesStudents"
import Dialog from "components/common/dialog/dialog"
import IconWrapper from "components/common/icon/iconWrapper"
import TextField from "components/form/common/field/textField"
import { useCustomSnackbar } from "hooks/snackbar"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { IGroup } from "ts/interfaces/Group"
import { IObjectStudentDetailed } from "ts/interfaces/Student"
import { requiredStringSchema } from "utils/yup"
import * as yup from "yup"

import { getGroupMutationFeedback } from "./getGroupMutationFeedback"
import { renderGroupMemberTags } from "./renderGroupMemberTags"
import { useGroupMembersListboxMaxHeight } from "./useGroupMembersListboxMaxHeight"

interface IFormValues {
  name: string
  members: IObjectStudentDetailed[]
}

const defaultValues: IFormValues = { name: "", members: [] }

const schema = yup.object({
  name: requiredStringSchema,
  members: yup.array().default([]),
})

interface IProps {
  open: boolean
  classId: number
  onClose: () => void
  onCreated?: (group: IGroup) => void
}

const CreateGroupDialog: FC<IProps> = ({
  open,
  classId,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const [search, setSearch] = useState("")
  const [isMembersFocused, setIsMembersFocused] = useState(false)
  const {
    listboxMaxHeight,
    onOpen: onListboxOpen,
    onClose: onListboxClose,
  } = useGroupMembersListboxMaxHeight()

  const methods = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  const { data, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQueryClassesStudents({
      classId,
      filter: search,
      options: { enabled: open && !!classId, keepPreviousData: true },
    })
  const options = data?.pages.flatMap((page) => page.data.objects) ?? []

  // Fetch from the observer callback, not an effect on `inView`: each new sentinel
  // reports its own visibility, so loading continues while it stays in view, and a
  // stale `inView` from the previous sentinel can't fire an extra page. A failed page
  // is retried only when the sentinel scrolls back into view.
  const { ref: loadMoreRef } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
  })

  const debouncedSetSearch = useCallback(debounce(setSearch, 300), [])

  const { mutate, isLoading } = useMutationCreateGroup({
    options: {
      onSuccess: (response) => {
        showSnackbar({ title: t("group.groupCreatedSuccess") })
        onCreated?.(response.data)
        handleClose()
      },
      onError: (error) => {
        const { onNameField, message, isRaw, nameError } =
          getGroupMutationFeedback(error)
        if (onNameField && nameError) {
          methods.setError("name", nameError)
          return
        }
        showSnackbar({
          title: isRaw ? message : t(message),
          variant: "error",
        })
      },
    },
  })

  const handleClose = () => {
    methods.reset(defaultValues)
    setSearch("")
    setIsMembersFocused(false)
    onClose()
  }

  const onSubmit = methods.handleSubmit((values) => {
    if (isLoading) return
    mutate({
      classId,
      data: {
        name: values.name,
        student_ids: values.members.map((member) => member.id),
      },
    })
  })

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      titleText={t("group.createNewGroup")}
      sx={(theme) => ({
        "& .MuiDialog-paper": {
          maxWidth: "870px",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.mockup.neutral90}`,
          backgroundColor: theme.palette.mockup.neutral100,
        },
      })}
      actionAcceptText={t("group.createGroupCta")}
      isLoading={isLoading}
      actionAcceptButtonProps={{ disabled: isLoading }}
      onActionButtonClick={onSubmit}
    >
      <Box>
        <FormProvider {...methods}>
          <Stack gap="24px">
            <TextField
              name="name"
              label={t("group.groupNameLabel")}
              placeholder={t("group.groupNameLabel")}
              fullWidth
            />
            <Controller
              name="members"
              control={methods.control}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  loading={isFetching}
                  options={options}
                  value={value}
                  ListboxProps={{
                    sx: { maxHeight: listboxMaxHeight },
                  }}
                  onOpen={onListboxOpen}
                  onClose={onListboxClose}
                  onFocus={() => setIsMembersFocused(true)}
                  onBlur={() => setIsMembersFocused(false)}
                  onChange={(_, newValue) => onChange(newValue)}
                  onInputChange={(_, newInputValue) =>
                    debouncedSetSearch(newInputValue)
                  }
                  isOptionEqualToValue={(option, val) => option.id === val.id}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  renderOption={(props, option, { selected }) => {
                    const { key: _key, ...optionProps } = props
                    const isLastOption =
                      option.id === options[options.length - 1]?.id
                    return (
                      <Fragment key={option.id}>
                        <Box component="li" {...optionProps}>
                          <Checkbox checked={selected} sx={{ mr: "8px" }} />
                          {option.first_name} {option.last_name}
                        </Box>
                        {isLastOption && hasNextPage && (
                          <Box ref={loadMoreRef} />
                        )}
                      </Fragment>
                    )
                  }}
                  renderTags={(members, getTagProps) =>
                    renderGroupMemberTags(
                      isMembersFocused,
                      members,
                      getTagProps,
                    )
                  }
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label={t("group.searchStudentLabel")}
                      placeholder={
                        value.length === 0
                          ? t("group.searchStudentLabel")
                          : undefined
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <IconWrapper>
                                <IconSearch />
                              </IconWrapper>
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Stack>
        </FormProvider>
      </Box>
    </Dialog>
  )
}

export default CreateGroupDialog
